'use client';

import React, { useEffect, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';

interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
}

export default function PageTransition({ children, className = '' }: PageTransitionProps) {
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [transitionState, setTransitionState] = useState<'entering' | 'visible' | 'exiting'>('entering');

  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
      setTransitionState('visible');
    }, 20);
    return () => clearTimeout(timer);
  }, []);

  // On route change, animate out then in
  useEffect(() => {
    setTransitionState('exiting');
    const t1 = setTimeout(() => {
      setDisplayChildren(children);
      setTransitionState('entering');
    }, 180);
    const t2 = setTimeout(() => {
      setTransitionState('visible');
    }, 220);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const style: React.CSSProperties = {
    opacity: transitionState === 'visible' ? 1 : 0,
    transform: transitionState === 'entering' ?'translateY(10px)'
      : transitionState === 'exiting' ?'translateY(-6px)' :'translateY(0)',
    transition: transitionState === 'visible' ?'opacity 320ms ease, transform 320ms ease'
      : transitionState === 'exiting' ?'opacity 160ms ease, transform 160ms ease' :'none',
  };

  return (
    <div className={className} style={style}>
      {displayChildren}
    </div>
  );
}

// ─── SCROLL REVEAL HOOK ───────────────────────────────────────────────────────
export function useScrollReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRevealed(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, revealed };
}

// ─── SCROLL REVEAL WRAPPER ────────────────────────────────────────────────────
interface ScrollRevealProps {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'left' | 'right' | 'fade';
  className?: string;
  style?: React.CSSProperties;
}

export function ScrollReveal({ children, delay = 0, direction = 'up', className = '', style = {} }: ScrollRevealProps) {
  const { ref, revealed } = useScrollReveal();

  const hiddenTransform =
    direction === 'up' ? 'translateY(28px)' :
    direction === 'left' ? 'translateX(-24px)' :
    direction === 'right' ? 'translateX(24px)' :
    'none';

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'none' : hiddenTransform,
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
